/**
 * DexieDeviceStore — concrete implementation of DeviceStore from
 * @argos/auth-client. Persists the encrypted device JWT + salt + IV +
 * WebAuthn credential ID in IndexedDB.
 *
 * The PIN itself never lands here; the AES-GCM key derived from the PIN
 * stays in SubtleCrypto. We only persist the wrapped (encrypted) JWT.
 */
import type { DeviceRecord, DeviceStore } from '@argos/auth-client';
import { getDb, type DeviceRow } from '../db';

function rowToRecord(r: DeviceRow): DeviceRecord {
  return {
    device_id: r.device_id,
    user: r.user as DeviceRecord['user'],
    paired_at: r.paired_at,
    expires_at: r.expires_at,
    unlock_method: r.unlock_method,
    salt: r.salt,
    iv: r.iv,
    wrapped_token: r.wrapped_token,
    pin_attempts: r.pin_attempts,
    credential_id: r.credential_id,
    prf_supported: r.prf_supported,
  };
}

function recordToRow(rec: DeviceRecord): DeviceRow {
  return {
    id: 'self',
    device_id: rec.device_id,
    user: rec.user,
    paired_at: rec.paired_at,
    expires_at: rec.expires_at,
    unlock_method: rec.unlock_method,
    salt: rec.salt,
    iv: rec.iv,
    wrapped_token: rec.wrapped_token,
    pin_attempts: rec.pin_attempts,
    credential_id: rec.credential_id,
    prf_supported: rec.prf_supported,
  };
}

export class DexieDeviceStore implements DeviceStore {
  async load(): Promise<DeviceRecord | null> {
    const row = await getDb().device.get('self');
    return row ? rowToRecord(row) : null;
  }

  async save(record: DeviceRecord): Promise<void> {
    await getDb().device.put(recordToRow(record));
  }

  async clear(): Promise<void> {
    await getDb().device.delete('self');
  }

  async incrementPinAttempts(): Promise<number> {
    const db = getDb();
    return db.transaction('rw', db.device, async () => {
      const row = await db.device.get('self');
      if (!row) throw new Error('No device loaded');
      const next = row.pin_attempts + 1;
      await db.device.update('self', { pin_attempts: next });
      return next;
    });
  }

  async resetPinAttempts(): Promise<void> {
    await getDb().device.update('self', { pin_attempts: 0 });
  }
}
